import React, { useState, useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { FaEllipsisH } from 'react-icons/fa';
import { DndContext, DragOverlay, MouseSensor, TouchSensor, useSensor, useSensors, useDraggable, useDroppable, closestCenter } from '@dnd-kit/core';
import { setSingleView, assignCameraToSlot, swapCameraSlots, refreshCloudAccess } from '../actions';
import { getBackend } from '../backend';
import { cameraDisplayLabel } from '../util/cameraLabel';
import CameraStreamContainerWs from './CameraStreamContainerWs';
import CameraStreamContainer from './CameraStreamContainer';
import CameraInfoOverlay from './CameraInfoOverlay';
import CameraSelectDialog from './CameraSelectDialog';
import TileActionsSheet from './TileActionsSheet';
import CameraTreePanel from './CameraTreePanel';
import { resolveDrag } from './dragResolve';
import ZoomableContainer from '../common/ZoomableContainer';
import { getProductConfig } from '../backend/productConfig';
import { getProductMode } from '../backend/productMode';
import { downloadScreenshot, downloadComposite } from '../util/screenshotHelper';
import { getOverlayPrefs, setOverlayPref, subscribeOverlayPrefs } from '../common/overlayPrefs';
import { isMobilePortrait } from './dragViewport';
import TileLoadingSpinner from './TileLoadingSpinner';
import { getVisibleCameras } from '../util/camSets';
import { gridColumns, tileSpan, arrangeGeometry } from '../util/layouts';
import { pageFillCounts } from '../util/arrangePages';
import { debugLog } from '../util/debug';

// Generate stable React key for camera (supports eView multi-NVR scenarios).
// CLOUD: key by device id so switching the active device (the cloud "jump")
// fully REMOUNTS each cell — a fresh WS + fresh decoder, identical to the initial
// load. Reusing an instance across devices left the old decoder configured for
// the previous stream and threw EncodingErrors (black video, and the previous
// device broke too). Local cams have no sDeviceID, so they keep their key.
export const getCameraKey = (camera) => {
    if (camera.compositeKey) return camera.compositeKey;
    if (camera.sDeviceID) return `dev_${camera.sDeviceID}_cam_${camera.bID}`;
    if (camera.bSerial) return `nvr_${camera.bSerial}_cam_${camera.bID}`;
    return `camera-${camera.bID}`;
};

// DraggableTile — the tile shell, made drag-swappable on touch (#86bb5dx62 L-D9). Each tile is
// BOTH a drag source and a drop target keyed by its grid position; dropping tile A on tile B swaps
// the two on-view cameras (VideoViewWs.handleDragEnd → swapCameraSlots by absolute index). The React
// `key` follows the CAMERA (getCameraKey) so a swap MOVES the live streams via keyed reconciliation
// rather than remounting them. `data-tile-slot` is kept for the ⋯ Snapshot DOM lookup. Drag is gated
// to the mobile-portrait viewport (dragEnabled) so desktop keeps its mouse zoom-select untouched.
const DraggableTile = ({ slotIndex, absIndex, dragEnabled, draggableDisabled, editMode, spanStyle, active, children }) => {
    const id = `live-slot-${slotIndex}`;
    // A filled tile is a drag source (tile↔tile swap) + a drop target. In Arrange an
    // empty cell passes draggableDisabled so it stays a drop target (drag a tree camera
    // onto it) without being liftable itself.
    const canLift = dragEnabled && !draggableDisabled;
    const draggable = useDraggable({ id, data: { absIndex }, disabled: !canLift });
    const droppable = useDroppable({ id, data: { absIndex }, disabled: !dragEnabled });
    const setRefs = (node) => { draggable.setNodeRef(node); droppable.setNodeRef(node); };
    // Arrange: the WHOLE tile is the drag activator (there is no digital zoom there, so no
    // collision). Resting LIVE grid on a MOUSE (fine pointer): only a small grip HANDLE
    // initiates the swap, leaving a click-drag on the video body free for digital zoom — so
    // zoom + swap coexist without a gesture collision. On a real TOUCH device (coarse pointer)
    // there is NO collision (pinch = zoom, long-press = swap), so we keep the plain long-press
    // body-drag and render NO handle — nothing obscures the video (David #86bb5dx62).
    const finePointer = typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia('(pointer: fine)').matches : true;
    const useHandle = canLift && !editMode && finePointer;
    const cls = 'live-tile'
        + (canLift && draggable.isDragging ? ' live-tile--dragging' : '')
        + (dragEnabled && droppable.isOver && !draggable.isDragging ? ' live-tile--drop-target' : '')
        + (active ? ' live-tile--active' : '');   // persists the accent border while the ⋯ menu is open
    return (
        <div
            ref={setRefs}
            data-tile-slot={slotIndex}
            className={cls}
            style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', outline: '1px solid #0a0e19', ...spanStyle }}
            {...(canLift && !useHandle ? draggable.attributes : {})}
            {...(canLift && !useHandle ? draggable.listeners : {})}
        >
            {children}
            {useHandle && (
                <button
                    className="live-tile-drag-handle"
                    aria-label="Move camera — drag to swap"
                    title="Drag to swap"
                    {...draggable.attributes}
                    {...draggable.listeners}
                    onClick={(e) => e.stopPropagation()}
                    onDoubleClick={(e) => e.stopPropagation()}
                >
                    <svg width="22" height="7" viewBox="0 0 22 7" fill="currentColor" aria-hidden="true"><rect x="4" y="1.5" width="14" height="1.4" rx="0.7" /><rect x="4" y="4.1" width="14" height="1.4" rx="0.7" /></svg>
                </button>
            )}
        </div>
    );
};

// ArrangeEmptyCell — a dashed drop target for an empty slot in Arrange mode (#86bb5dx62).
// Droppable (a tree camera dropped here assigns into absIndex) but never a drag source,
// and it shows the full density cell (no shrink) so the grid reads as a template.
const ArrangeEmptyCell = ({ slotIndex, absIndex, spanStyle }) => {
    const droppable = useDroppable({ id: `live-slot-${slotIndex}`, data: { absIndex } });
    return (
        <div
            ref={droppable.setNodeRef}
            data-tile-slot={slotIndex}
            className={'live-arrange-empty' + (droppable.isOver ? ' live-arrange-empty--over' : '')}
            style={spanStyle || undefined}
        >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" /></svg>
            <span>Drop camera</span>
        </div>
    );
};


const VideoViewWs = props => {
    const { cameras, conf, currentCamView, fSingle, singleCamSelected, setSingleView,
            onWebSocketFailure, onWebSocketSuccess, sSess, wsBackendSupported,
            isJumping, targetSystemName, assignCameraToSlot, swapCameraSlots, fEview, useEviewLogin, serverSessions, onContextMenuOpen,
            nvrTimestamp, username, password, productMode, editMode, arrange } = props;

    // Arrange mode (#86bb5dx62) has its OWN pager: density = tiles per page, page =
    // which window of the 64-slot cameras array. It is independent of the live conf /
    // currentCamView so entering / leaving Arrange never disturbs the resting grid.
    const arrangeDensity = (arrange && arrange.density) || 8;
    const arrangePage = (arrange && arrange.page) || 0;

    // Detect eView mode (multi-NVR)
    const isEviewMode = fEview || useEviewLogin;

    // Live overlay prefs — subscribe so the ⋯ tile-menu toggle LABELS ("Show/Hide camera info", "…name &
    // timestamp") reflect the current state immediately, the SAME subscribed-state pattern the overlays
    // gear uses (CameraOverlaysContent). Both entry points drive the one shared setOverlayPref, so they
    // behave identically and a stale label can't cause a confusing double-toggle. #86baz05kd (David 2026-08-16).
    const [ovPrefs, setOvPrefs] = useState(getOverlayPrefs);
    useEffect(() => subscribeOverlayPrefs(setOvPrefs), []);

    // Live-grid transport model per product. LOCAL keeps the hybrid JPEG-base +
    // WS-overlay path (a JPEG always renders under the WS). CLOUD has no mpengine
    // JPEG source, so each cell is a single WebCodecs player — EVERY cloud device
    // (dragon and legacy alike) streams raw H.264 over the token-authed WS
    // (verified vs prod 2026-07-16), so there is one player, no per-device split.
    // Named capability, not an `if (cloud)` — see productConfig.
    const hasJpegBase = getProductConfig({ productMode }).hasJpegBaseLayer;

    // Format NVR timestamp for overlay
    const formatTimestamp = (ts) => {
        if (!ts) return '';
        const d = new Date(ts);
        if (isNaN(d.getTime())) return '';
        return ('0' + d.getHours()).slice(-2) + ':' + ('0' + d.getMinutes()).slice(-2) + ':' + ('0' + d.getSeconds()).slice(-2);
    };

    // Each tile shows its OWN camera stream's time — the per-tile desync signal
    // (#86bb5dx62). Held in a ref, not state, so per-frame metadata never triggers a
    // re-render; the 1s nvrTimestamp re-render flushes the displayed values.
    const camTimesRef = useRef({});

    // Simple state: which cameras are using WS vs JPEG
    const [streamTypes, setStreamTypes] = useState({});

    // Track which cameras have WS actively streaming (first frame received)
    const [wsStreaming, setWsStreaming] = useState({});

    // The tile's displayed time. Trust the WS stream time ONLY while this cell is actually painting WS
    // frames (wsStreaming===true). The instant it falls back to the JPEG base — WS stalled, disconnected,
    // or never upgraded — that WS time is stale, so show the live NVR clock (which re-renders every 1s) so
    // the overlay NEVER freezes. Reactive to WS upgrade/fallback (wsStreaming is state) and independent
    // per cell, so a MIXED WS+JPEG view shows each cell its own correct, live time. Was: used camTimesRef
    // whenever it had ANY value → a member that connected once then delivered 0 frames left a frozen time
    // that never cleared on the WS→JPEG fallback. #86bb7phhh (David 2026-08-17).
    // Keyed by getCameraKey (nvr_<serial>_cam_<bID>), NOT the raw camera.id. camera.id is the per-NVR
    // mpengine camera number — it feeds /ws/cam<id>-pro<n> — so in an eView grid two members that both
    // have camera 1 collide and the last metadata update wins, painting one member's clock onto the
    // other's tile. This file already defines getCameraKey for exactly that reason. #86bb7phhh
    const tileTimestamp = (cam) => {
        const k = getCameraKey(cam);
        const t = wsStreaming[k] === true ? camTimesRef.current[k] : null;
        return t ? (t.split(' ')[1] || '') : formatTimestamp(nvrTimestamp);
    };

    // Track jump transition - show overlay until first frame arrives after jump
    const [awaitingFirstFrameAfterJump, setAwaitingFirstFrameAfterJump] = useState(false);
    const prevIsJumping = useRef(false);

    // Track retry timers per camera
    const retryTimersRef = useRef({});
    const mountedRef = useRef(true);
    const upgradeQueueRef = useRef([]);
    const upgradeTimerRef = useRef(null);

    const [selectDialog, setSelectDialog] = useState({
        visible: false,
        slotIndex: null
    });

    // ⋯ tile-actions sheet — opened by the ⋯ button AND by right-click (both open this ONE menu;
    // the old separate desktop CameraContextMenu was removed). Snapshot / Assign / Clear /
    // Toggle-Info per slot (#86bb5dx62 L-D1).
    const [tileSheet, setTileSheet] = useState({
        visible: false,
        slotIndex: null,
        camera: null,
        anchor: null   // desktop: the ⋯ button rect → anchored popover; mobile: null → bottom sheet
    });

    // The camera currently being dragged — drives the DragOverlay preview (#86bb5dx62,
    // David): a boxed chip that follows the pointer/finger so you can see you're carrying
    // something. { num, name } or null. Set on drag start, cleared on end/cancel.
    const [activeDrag, setActiveDrag] = useState(null);

    // Double-click/tap fires onDoubleClick on BOTH the inner stream layer AND the wrapping
    // ZoomableContainer (nested handlers) — two synchronous calls per gesture. The old
    // setSingleView was idempotent so it never mattered; the conf-8 swap is not (two swaps
    // cancel). Dedupe: ignore a repeat within 150ms so one gesture = one action (mouse + touch).
    const dblClickGuard = useRef(0);

    // Arrange page-tabs overflow → show ‹ › scroll arrows (mirrors the camera-bank strip).
    const tabsScrollRef = useRef(null);
    const [tabsOverflow, setTabsOverflow] = useState(false);

    // Re-render when a grid-adapting viewport condition flips, so the geometry re-applies
    // immediately on rotate / resize. Watches the landscape (<=1279) and portrait (<=1023)
    // boundaries so conf-2 (portrait stack 1x2 vs side-by-side 2x1) and conf-6/12 (portrait
    // transpose 2x3 / 3x4 vs 3x2 / 4x3, #86bb7phhh A2) re-apply. Cheap: just bumps a counter;
    // streams keep their camera-keyed identity, so they resize rather than remount.
    const [, setOrientationTick] = useState(0);
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const mqs = [
            window.matchMedia('(orientation: landscape) and (max-width: 1279px)'),
            window.matchMedia('(max-width: 1023px) and (orientation: portrait)'),
        ];
        const onChange = () => setOrientationTick(n => n + 1);
        mqs.forEach((mq) => {
            if (mq.addEventListener) mq.addEventListener('change', onChange);
            else if (mq.addListener) mq.addListener(onChange);
        });
        return () => {
            mqs.forEach((mq) => {
                if (mq.removeEventListener) mq.removeEventListener('change', onChange);
                else if (mq.removeListener) mq.removeListener(onChange);
            });
        };
    }, []);

    // Right-click opens the SAME ⋯ tile-actions menu (David) — the legacy right-click gesture is
    // kept, but pointed at the one menu instead of a separate, redundant CameraContextMenu. Desktop
    // → a popover anchored at the cursor; touch → the bottom sheet. Mirrors handleOpenTileSheet
    // (setTileSheet inline so there is no definition-order dependency between the two callbacks).
    const handleContextMenu = useCallback((e, slotIndex, camera) => {
        e.preventDefault();
        e.stopPropagation();
        if (onContextMenuOpen) onContextMenuOpen();   // stop auto-scan while the menu is open
        const desktop = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(min-width: 1280px)').matches;
        const anchor = desktop ? { top: e.clientY, bottom: e.clientY, left: e.clientX, right: e.clientX } : null;
        setTileSheet({ visible: true, slotIndex, camera, anchor });
    }, [onContextMenuOpen]);

    const handleAssignCamera = useCallback((slotIndex) => {
        setSelectDialog({
            visible: true,
            slotIndex
        });
    }, []);

    const handleClearSlot = useCallback((slotIndex) => {
        debugLog('[VideoViewWs] Clearing slot', slotIndex);
        assignCameraToSlot(slotIndex, null);
    }, [assignCameraToSlot]);

    const handleSelectCamera = useCallback((camera) => {
        debugLog('[VideoViewWs] Assigning camera', camera.bID, 'to slot', selectDialog.slotIndex);
        assignCameraToSlot(selectDialog.slotIndex, camera);
        setSelectDialog({ visible: false, slotIndex: null });
    }, [assignCameraToSlot, selectDialog.slotIndex]);

    const handleCloseSelectDialog = useCallback(() => {
        setSelectDialog({ visible: false, slotIndex: null });
    }, []);

    // ── ⋯ tile-actions sheet handlers (mobile) ──────────────────────────────
    const handleOpenTileSheet = useCallback((slotIndex, camera, anchorRect) => {
        // Stop auto-scan while the menu is open (mirrors the right-click path)
        if (onContextMenuOpen) onContextMenuOpen();
        // Desktop → a compact popover anchored to the ⋯ (a full-width bottom sheet is a mobile
        // pattern, wrong for a 4-item menu on desktop — David). Touch/mobile → the bottom sheet.
        const desktop = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(min-width: 1280px)').matches;
        const anchor = (desktop && anchorRect) ? { top: anchorRect.top, bottom: anchorRect.bottom, left: anchorRect.left, right: anchorRect.right } : null;
        setTileSheet({ visible: true, slotIndex, camera, anchor });
    }, [onContextMenuOpen]);

    const handleCloseTileSheet = useCallback(() => {
        setTileSheet({ visible: false, slotIndex: null, camera: null, anchor: null });
    }, []);

    const handleTileSnapshot = () => {
        const idx = tileSheet.slotIndex;
        const cam = tileSheet.camera;
        handleCloseTileSheet();
        if (idx == null) return;
        const tileEl = document.querySelector(`[data-tile-slot="${idx}"]`);
        // Prefer the WebCodecs <canvas> (untainted) over a cross-origin <img> — a JPEG frame
        // can taint the canvas so toDataURL throws; once a tile upgrades to WS the canvas exists.
        const source = tileEl && (tileEl.querySelector('canvas') || tileEl.querySelector('video') || tileEl.querySelector('img'));
        const camName = cam ? (cam.camName || cam.sName || '') : '';
        const timestamp = cam ? tileTimestamp(cam) : '';
        try {
            downloadScreenshot(source, { camName, timestamp });
        } catch (e) {
            debugLog('[VideoViewWs] snapshot failed (canvas may be cross-origin tainted):', e && e.message);
        }
    };

    const handleSnapshotAll = () => {
        handleCloseTileSheet();
        const tiles = document.querySelectorAll('[data-tile-slot]');
        const cols = Math.ceil(Math.sqrt(tiles.length)) || 1;
        const cells = Array.from(tiles).map(tile => {
            const canvas = tile.querySelector('canvas');
            if (!canvas || !canvas.width) return null;
            const nameEl = tile.querySelector('.live-overlay__text');
            const timeEl = tile.querySelector('.live-overlay__time');
            const camName = nameEl ? nameEl.textContent || '' : '';
            const timestamp = timeEl ? timeEl.textContent || '' : '';
            return { source: canvas, camName, timestamp };
        });
        const hasFrame = cells.some(c => c && c.source && c.source.width);
        const stamp = new Date().toISOString().replace(/[:.]/g, '').slice(0, 15);
        const ok = downloadComposite(cells, { cols, filename: `live_grid_${stamp}.png` });
        if (!ok && !hasFrame) debugLog('[VideoViewWs] composite snapshot: no frames to capture');
    };

    const handleTileToggleInfo = () => {
        // Global toggle (all tiles), matching Java VideoController.java:1116 — flips the shared
        // overlayPrefs.info that the Setup toggle + every CameraInfoOverlay already observe.
        setOverlayPref('info', !getOverlayPrefs().info);
        handleCloseTileSheet();
    };

    const handleTileToggleMeta = () => {
        // Global toggle (all tiles) — flips the shared overlayPrefs.metadata (the FPS/bitrate/codec/
        // resolution "Camera Info" readout) that the desktop overlays gear / Options + every
        // CameraInfoOverlay already observe. Same flag, just a second entry point (David 2026-08-16).
        setOverlayPref('metadata', !getOverlayPrefs().metadata);
        handleCloseTileSheet();
    };

    const handleTileAssign = () => {
        const idx = tileSheet.slotIndex;
        handleCloseTileSheet();
        handleAssignCamera(idx);
    };

    const handleTileClear = () => {
        const idx = tileSheet.slotIndex;
        handleCloseTileSheet();
        handleClearSlot(idx);
    };

    // Track jump state transitions to keep overlay until first frame arrives
    useEffect(() => {
        // When jump starts, prepare to wait for first frame
        if (isJumping && !prevIsJumping.current) {
            setAwaitingFirstFrameAfterJump(true);
            // Clear streaming state so we know we need new frames
            setWsStreaming({});
        }
        // When jump ends (success or failure), set a timeout to clear overlay
        // If first frame arrives before timeout, it clears immediately
        // If jump failed, this ensures overlay doesn't get stuck
        if (!isJumping && prevIsJumping.current) {
            const failsafeTimeout = setTimeout(() => {
                setAwaitingFirstFrameAfterJump(false);
            }, 3000); // 3 second failsafe
            // Cleanup timeout if component unmounts or new jump starts
            return () => clearTimeout(failsafeTimeout);
        }
        prevIsJumping.current = isJumping;
    }, [isJumping]);

    // Calculate decoder profile (separate from mpengine stream profile)
    // decoderProfile 1 (1-9 cams): auto-detect decoder, switch to software if pumping
    // decoderProfile 2 (12+ cams): force hardware to save CPU, accept minor pumping
    const isLargeGrid = editMode ? arrangeDensity >= 12 : ["conf-12", "conf-16", "conf-32"].includes(conf);
    const decoderProfile = (fSingle || !isLargeGrid) ? 1 : 2;

    // Get active cameras for current view
    const activeCameras = (() => {
        if (!cameras || !Array.isArray(cameras) || cameras.length === 0) return [];

        let camerasToShow = [];
        if (editMode) {
            // Arrange window: the density-sized slice at the current page. Slots keep
            // their absolute index (viewStart) so assign/swap/clear hit the right cell.
            const start = arrangePage * arrangeDensity;
            camerasToShow = cameras.slice(start, start + arrangeDensity);
        } else if (fSingle) {
            const cam = cameras.find(c => c.bID === singleCamSelected);
            if (cam) camerasToShow = [cam];
        } else {
            // The current SET (bank) — the SAME slice playback uses, via the shared selector (#86bb7phhh).
            camerasToShow = getVisibleCameras(cameras, currentCamView);
        }

        // Stream profile: 1 = high quality (1-9 cams), 2 = lower quality (12+ cams)
        // This is separate from decoderProfile (hardware vs software decoding)
        const isSmallGrid = editMode ? arrangeDensity <= 9 : ["conf-1", "conf-2", "conf-4", "conf-6", "conf-8", "conf-9"].includes(conf);
        const streamProfile = (fSingle || isSmallGrid) ? 1 : 2;

        // The active backend builds each camera's live URL; this call site stays
        // mode-agnostic. LocalNvrAdapter resolves eView-vs-single-NVR from this
        // ctx (per-NVR user:pass fallback for cross-NVR cameras whose
        // serverSessions entry has sSess=null — the typical eView case, see
        // NavigationActions.js:230); CloudNvrAdapter ignores ctx and builds the
        // dragon WS URL from the device index it cached at login.
        const backend = getBackend();
        const liveCtx = { isEview: isEviewMode, serverSessions, session: sSess, username, password };

        return camerasToShow.map((c, i) => {
            const wsUrl = backend.buildLiveUrl(c, streamProfile, liveCtx);

            return {
                id: c.bID,
                wsUrl,
                // Shared Live/Playback label helper — eView "Server : Cam", local "id - Cam" (#86bb7phhh).
                camName: cameraDisplayLabel(c, { isEview: isEviewMode }),
                enabled: c.fEnable,
                onDoubleClick: () => {
                    // Dedupe the nested inner+wrapper double-fire (same gesture) so a swap isn't undone.
                    const now = performance.now();
                    if (now - dblClickGuard.current < 150) return;
                    dblClickGuard.current = now;
                    // In single-cam view, double-click restores the grid.
                    if (fSingle) { setSingleView(''); return; }
                    if (!c.fEnable) return;
                    // conf-8 bubble (L-D5, David): the layout is 1 big (position 0) + 7 small. A
                    // SMALL tile (i > 0) promotes into the big slot (swap the two cells); only the
                    // BIG tile drills to single-cam. Every other conf: double-click → single-cam.
                    // (isConf8 / viewStart are defined lower in this render; the closure reads them
                    // at call time, after the render has initialized them.)
                    if (isConf8 && i > 0) swapCameraSlots(viewStart + 0, viewStart + i);
                    else setSingleView(c.bID);
                },
                ...c
            };
        });
    })();

    // Signature of WHICH cameras are on screen, by the same composite identity every per-camera map
    // here is keyed on. The init effect above seeds streamTypes/the upgrade queue from these keys, and
    // its other deps (conf, count, currentCamView…) do NOT change when an eView user switches between
    // two views of the same size: the raw camera numbers used to repeat across views, so the old
    // entries still matched, but composite keys do not. Without this the new view's cameras have no
    // streamTypes entry, useWs stays false, and they sit on the JPEG base with no WS metadata or object
    // overlays. #86bb7phhh
    const camKeySig = (activeCameras || []).map(getCameraKey).join(',');


    // Process upgrade queue one camera at a time
    const processUpgradeQueue = useCallback(() => {
        if (!mountedRef.current) return;
        if (upgradeQueueRef.current.length === 0) return;

        const camId = upgradeQueueRef.current.shift();

        setStreamTypes(prev => {
            if (prev[camId] === 'ws') return prev; // Already upgraded
            return { ...prev, [camId]: 'ws' };
        });

        // Schedule next upgrade
        if (upgradeQueueRef.current.length > 0) {
            upgradeTimerRef.current = setTimeout(processUpgradeQueue, 500);
        }
    }, []);

    // Initialize: Start fresh when view/profile changes
    // Try WS for all enabled cameras (staggered)
    useEffect(() => {
        mountedRef.current = true;

        // Clear all retry timers
        Object.values(retryTimersRef.current).forEach(t => clearTimeout(t));
        retryTimersRef.current = {};

        // Clear upgrade queue
        clearTimeout(upgradeTimerRef.current);
        upgradeQueueRef.current = [];

        if (!activeCameras || activeCameras.length === 0) {
            return () => { mountedRef.current = false; };
        }

        // Start fresh — cloud (no JPEG base) starts all cameras directly on WS;
        // local starts on JPEG and queues staggered WS upgrades so the JPEG base
        // is visible immediately while WS connections ramp up one at a time.
        const initial = {};
        if (!hasJpegBase && wsBackendSupported) {
            // Cloud: no JPEG base — start enabled cameras directly on WS. Preserve wsStreaming for
            // cameras already streaming (blanking it flashes a spinner over live video); only NEW
            // cameras start without it. Keyed by getCameraKey (collision-safe across cloud devices).
            setWsStreaming(prev => {
                const kept = {};
                const enabledKeys = new Set(activeCameras.filter(c => c.enabled).map(getCameraKey));
                for (const key of enabledKeys) {
                    if (prev[key]) kept[key] = prev[key];
                }
                return kept;
            });
            const enabledCams = activeCameras.filter(c => c.enabled);
            activeCameras.forEach(cam => {
                initial[getCameraKey(cam)] = enabledCams.includes(cam) ? 'ws' : 'jpeg';
            });
            setStreamTypes(initial);
        } else {
            // Local/eview: JPEG base is visible underneath, so clear wsStreaming and let the
            // staggered WS upgrade queue re-set it per camera.
            setWsStreaming({});
            activeCameras.forEach(cam => {
                initial[getCameraKey(cam)] = 'jpeg';
            });
            setStreamTypes(initial);

            if (wsBackendSupported) {
                const enabledCams = activeCameras.filter(c => c.enabled).map(getCameraKey);
                upgradeQueueRef.current = [...enabledCams];
                upgradeTimerRef.current = setTimeout(processUpgradeQueue, 300);
            }
        }

        return () => {
            mountedRef.current = false;
            Object.values(retryTimersRef.current).forEach(t => clearTimeout(t));
            clearTimeout(upgradeTimerRef.current);
        };
    // sSess: a Jump System / re-login swaps the active NVR — re-queue the WS upgrades
    // so they reconnect against the NEW host (buildWebSocketUrl reads the freshly
    // committed jumpedServerUrl), instead of streaming the previous server's WS.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conf, fSingle, currentCamView, singleCamSelected, decoderProfile, wsBackendSupported, cameras?.length, sSess, camKeySig]);

    // Handle WS success
    const handleWsSuccess = useCallback((cameraId) => {
        if (!mountedRef.current) return;

        // Clear any pending retry
        if (retryTimersRef.current[cameraId]) {
            clearTimeout(retryTimersRef.current[cameraId]);
            delete retryTimersRef.current[cameraId];
        }

        if (onWebSocketSuccess) onWebSocketSuccess();
    }, [onWebSocketSuccess]);

    // Handle WS failure - retry with backoff
    // `cameraId` here is the COMPOSITE camera key (getCameraKey), not the raw mpengine camera number.
    // Every per-camera map in this component — streamTypes, wsStreaming, retryTimersRef, camTimesRef,
    // the upgrade queue — is keyed that way, because the raw number is only unique WITHIN one NVR: in an
    // eView grid two members each having a camera 1 shared one entry, so one member's WS fallback flipped
    // the other member's tile to JPEG and blanked its clock. Only the /ws/cam<n>-pro<p> URL uses the raw
    // number (cameraId={camera.id} below), which is correct — that is per-NVR by definition. #86bb7phhh
    const handleWsFailure = useCallback((cameraId) => {
        if (!mountedRef.current) return;

        // Clear any existing retry timer
        if (retryTimersRef.current[cameraId]) {
            clearTimeout(retryTimersRef.current[cameraId]);
        }

        setStreamTypes(prev => {
            if (prev[cameraId] === 'jpeg') return prev;
            return { ...prev, [cameraId]: 'jpeg' };
        });

        // Schedule retry: 2s for first, then 30s
        const retryDelay = retryTimersRef.current[cameraId + '_count'] ? 30000 : 2000;
        retryTimersRef.current[cameraId + '_count'] = (retryTimersRef.current[cameraId + '_count'] || 0) + 1;

        retryTimersRef.current[cameraId] = setTimeout(() => {
            if (!mountedRef.current) return;
            setStreamTypes(prev => {
                if (prev[cameraId] === 'ws') return prev;
                return { ...prev, [cameraId]: 'ws' };
            });
        }, retryDelay);

        // Clear streaming flag
        setWsStreaming(prev => {
            if (!prev[cameraId]) return prev;
            return { ...prev, [cameraId]: false };
        });

        // Drop this cam's now-stale WS stream time so the tile's overlay reads the live NVR clock while
        // it's on the JPEG base (not a frozen last-WS-time). Re-populated by onMetadataUpdate if WS
        // re-upgrades. Belt-and-suspenders with the wsStreaming gate in tileTimestamp. #86bb7phhh.
        delete camTimesRef.current[cameraId];

        if (onWebSocketFailure) onWebSocketFailure();
    }, [onWebSocketFailure]);

    // Handle WS first frame - stop JPEG polling and clear jump overlay
    const handleWsFirstFrame = useCallback((cameraId) => {
        if (!mountedRef.current) return;
        setWsStreaming(prev => {
            if (prev[cameraId]) return prev;
            return { ...prev, [cameraId]: true };
        });
        // First frame after jump received - clear the overlay
        setAwaitingFirstFrameAfterJump(false);
    }, []);

    // Re-arm this tile's loading spinner when its stream (re)connects. The cloud effect PRESERVES
    // wsStreaming across view/grid switches (so a still-live tile doesn't flash), but that goes stale
    // when a tile actually remounts/reconnects — the spinner then shows ONLY on the first view and stays
    // suppressed afterwards. CameraStreamContainerWs.onConnecting fires on its [wsUrl, cameraId] reset,
    // so drop the stale `true` here and let handleWsFirstFrame re-set it on the next frame. (#86bbm58f7)
    const handleWsConnecting = useCallback((cameraId) => {
        if (!mountedRef.current) return;
        setWsStreaming(prev => {
            if (!(cameraId in prev)) return prev;
            const next = { ...prev };
            delete next[cameraId];
            return next;
        });
    }, []);

    // Grid layout. Live geometry comes from the centralized LAYOUTS config (util/layouts),
    // so raising the ceiling is a one-line data change. Arrange overrides it with the
    // symmetric density grid (4→2×2, 8→4×2, 16→4×4) and always shows the FULL page of
    // cells (empty ones become drop targets), so its rows are fixed, not content-fit.
    const arrangeGeo = editMode ? arrangeGeometry(arrangeDensity) : null;
    const gridCols = (() => {
        if (editMode) return arrangeGeo.cols;
        if (fSingle) return 1;
        // conf-2 stacks (2->1) in portrait (mobile + tablet portrait, <=1023) and is side-by-side
        // otherwise (landscape + desktop); conf-6/12 transpose to fewer columns in the same portrait
        // tiers (2x3 / 3x4, #86bb7phhh A2); every other conf is orientation-agnostic.
        return gridColumns(conf, { portrait: isMobilePortrait() });
    })();
    // conf-8 is a fixed 4×4 asymmetric layout (big top-left 3×3 + 7 small wrapping),
    // mirroring Java VideoView.java:745. Force 4 rows so the big tile always has room,
    // even with fewer than 8 cameras present. Arrange never uses conf-8's asymmetry.
    const isConf8 = conf === 'conf-8' && !fSingle && !editMode;
    const gridRows = editMode ? arrangeGeo.rows : (isConf8 ? 4 : Math.ceil(activeCameras.length / gridCols));
    // The first tile in conf-8 spans the top-left 3×3 block; grid auto-placement
    // then wraps the remaining 7 small tiles down the right column and along the
    // bottom row (7 = 3 + 4). Driven off the config's span for that conf.
    const conf8Span = tileSpan(conf, { portrait: isMobilePortrait() });
    const tileSpanStyle = (index) => (isConf8 && index === 0 && conf8Span)
        ? { gridColumn: `span ${conf8Span.cols}`, gridRow: `span ${conf8Span.rows}` } : null;

    // Drag-to-arrange on-view cameras (#86bb5dx62 L-D9). Drag a tile onto another to swap.
    // Dual-input drag (David): MOUSE grabs immediately (after a 6px move — snappy, no wait);
    // TOUCH uses a 250ms long-press so a tap / scroll / double-tap-zoom isn't mistaken for a
    // drag. dnd-kit auto-selects the sensor by input type, so both mouse and touch work.
    const dragSensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } })
    );
    // Drag enabled in Arrange always (tree→tile assign + tile↔tile swap), and in the resting
    // Live grid on EVERY viewport when >1 camera is on view (David: tablet + desktop get live
    // tile drag-swap too, mouse and touch). The MouseSensor's 6px activation leaves a plain
    // click / double-click-promote / right-click-assign untouched; TouchSensor's long-press
    // leaves tap / double-tap-zoom / swipe-page untouched.
    const dragEnabled = editMode ? true : (!fSingle && activeCameras.length > 1);
    // Absolute index of the first on-view slot (page window start), so a swap/assign targets the
    // right positions in the full cameras array even on page 2+ ("cam_5-8" → 4; Arrange page 2 @16 → 16).
    const viewStart = (() => {
        if (editMode) return arrangePage * arrangeDensity;
        if (fSingle) return 0;
        const parts = (currentCamView || 'cam_1').replace('cam_', '').split('-');
        const s = parseInt(parts[0], 10);
        return isNaN(s) ? 0 : s - 1;
    })();
    // One DndContext, two drag sources over the same tile drop targets: a tile dragged onto
    // another tile swaps them; a camera dragged out of the Arrange tree assigns into that slot.
    // resolveDrag (pure, unit-tested) discriminates by active.data.current.type.
    // On drag start, capture what's being dragged so the DragOverlay can preview it:
    // a tree row carries { type:'tree-camera', camera }; a tile carries { absIndex } → look
    // up the camera occupying that slot.
    const handleDragStart = (event) => {
        const d = event.active && event.active.data && event.active.data.current;
        if (!d) { setActiveDrag(null); return; }
        if (d.type === 'tree-camera' && d.camera) {
            setActiveDrag({ num: d.camera.bID, name: d.camera.sName });
        } else if (d.absIndex != null) {
            const cam = (cameras || [])[d.absIndex];
            setActiveDrag(cam && cam.fEnable ? { num: cam.bID, name: cam.sName } : null);
        } else {
            setActiveDrag(null);
        }
    };

    const handleDragEnd = (event) => {
        setActiveDrag(null);
        const move = resolveDrag(event.active, event.over);
        if (!move) return;
        if (move.kind === 'assign') assignCameraToSlot(move.slot, move.camera);
        else if (move.kind === 'swap') swapCameraSlots(move.from, move.to);
    };

    const handleDragCancel = () => setActiveDrag(null);

    // Arrange page tabs are the pager in this mode (the live camera-bank strip is hidden).
    // Each tab shows "Page N x/density" — the filled count per page, computed from the full
    // 64-slot array — and, once there is more than one page, a ✕ to delete it.
    const arr = arrange || {};
    const arrangePageCount = arr.pageCount || 1;
    const arrangeFills = editMode ? pageFillCounts(cameras || [], arrangeDensity, arrangePageCount) : [];

    // Show ‹ › arrows when the page-tabs strip overflows (many pages at low density / big
    // views), mirroring the camera-bank strip. Re-measure on tab-set + resize changes.
    useEffect(() => {
        if (!editMode) return undefined;
        const measure = () => { if (tabsScrollRef.current) setTabsOverflow(tabsScrollRef.current.scrollWidth > tabsScrollRef.current.clientWidth + 4); };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editMode, arrangePageCount, arrangeDensity]);
    const scrollTabs = (dir) => {
        const el = tabsScrollRef.current;
        if (el) el.scrollBy({ left: dir * Math.max(120, Math.round(el.clientWidth * 0.7)), behavior: 'smooth' });
    };

    return (
        <div style={{ height: '100%', width: '100%', position: 'relative' }}>
            <DndContext sensors={dragSensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
            <div className={'live-editwrap' + (editMode ? ' live-editwrap--on' : '')}>
            {/* Arrange: the camera tree, or a thin tappable "Cameras" rail when collapsed
                (best in tablet portrait — reclaims width without hiding the source). */}
            {editMode && (arr.treeCollapsed ? (
                <button type="button" className="live-edit-rail" onClick={arr.onToggleTree} aria-label="Show camera list">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M9.4 18.4 8 17l5-5-5-5 1.4-1.4L15.8 12z" /></svg>
                    <span className="live-edit-rail__label">Cameras</span>
                </button>
            ) : <CameraTreePanel onCollapse={arr.onToggleTree} />)}

            {/* Canvas column: the Arrange page-tabs row sits above the grid. At rest the
                canvas is display:contents (a layout passthrough via CSS), so the resting
                grid stays a direct child of .live-editwrap — byte-identical to before. */}
            <div className="live-arrange-canvas">
                {editMode && (
                <div className="live-arrange-tabs">
                    {/* No tree-toggle here — the thin "Cameras" rail (shown when collapsed)
                        is the single re-expand affordance, so this row stays uncluttered. */}
                    {tabsOverflow && (
                        <button type="button" className="live-arrange-tabs__arrow" onClick={() => scrollTabs(-1)} aria-label="Scroll pages left">‹</button>
                    )}
                    <div className="live-arrange-tabs__scroll" ref={tabsScrollRef}>
                        {Array.from({ length: arrangePageCount }, (_, p) => (
                            <button
                                key={p}
                                type="button"
                                className={'live-arrange-tab' + (p === arrangePage ? ' live-arrange-tab--active' : '')}
                                aria-pressed={p === arrangePage}
                                onClick={() => arr.onSelectPage && arr.onSelectPage(p)}
                            >
                                Page {p + 1}
                                <span className="live-arrange-tab__fill">{arrangeFills[p] || 0}/{arrangeDensity}</span>
                                {arrangePageCount > 1 && (
                                    <span
                                        className="live-arrange-tab__del"
                                        role="button"
                                        aria-label={`Delete page ${p + 1}`}
                                        title="Delete page"
                                        onClick={(e) => { e.stopPropagation(); arr.onDeletePage && arr.onDeletePage(p); }}
                                    >×</span>
                                )}
                            </button>
                        ))}
                    </div>
                    {tabsOverflow && (
                        <button type="button" className="live-arrange-tabs__arrow" onClick={() => scrollTabs(1)} aria-label="Scroll pages right">›</button>
                    )}
                    <button
                        type="button"
                        className="live-arrange-tabs__add"
                        onClick={() => arr.onAddPage && arr.onAddPage()}
                        disabled={!arr.canAddPage}
                        title={arr.canAddPage ? 'Add page' : 'Maximum pages reached'}
                    >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" /></svg>
                        <span className="live-arrange-tabs__add-label">Add page</span>
                    </button>
                </div>
                )}
            <div className={'ip-camera-grid' + ((conf === 'conf-1' || fSingle) ? ' ip-camera-grid--single' : '')} style={{
                ...styles.containerStyle,
                gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
                gridTemplateRows: `repeat(${gridRows}, minmax(0, 1fr))`,
                // Arrange is a light EDITOR, not the live-video wall: the grid's black
                // (styles.containerStyle) was showing through each empty slot's margins as black
                // bars between cells. In arrange paint the grid with the canvas surface, and use a
                // single grid GAP for spacing instead of per-cell margins — the old empty-cell
                // margins DOUBLED between two empty slots (3px+3px=6px) yet were 0 between filled
                // tiles (which have no margin), so gaps read inconsistently. One 4px gap + matching
                // padding = a uniform inset on every side, filled or empty (#86bb5dx62). Live view
                // keeps the black + margin-less butted tiles. Overrides the inline values above.
                ...(editMode ? { backgroundColor: 'var(--theme-bg-primary)', gap: '4px', padding: '4px' } : {})
            }}>
                {activeCameras.slice(0, gridCols * gridRows).map((camera, index) => {
                    // Arrange mode: an empty (disabled) slot is a dashed DROP TARGET, not a
                    // video tile — the grid reads as a fillable template (#86bb5dx62).
                    if (editMode && !camera.enabled) {
                        return (
                            <ArrangeEmptyCell
                                key={`arrange-empty-${viewStart + index}`}
                                slotIndex={index}
                                absIndex={viewStart + index}
                                spanStyle={tileSpanStyle(index)}
                            />
                        );
                    }
                    // CLOUD: no JPEG base. One WebCodecs player per cell — every
                    // cloud device (dragon + legacy) streams raw H.264 over the
                    // token-authed WS, so CameraStreamContainerWs handles them all;
                    // it self-manages connect/reconnect with no JPEG fallback layer.
                    if (!hasJpegBase) {
                        return (
                            <DraggableTile
                                key={getCameraKey(camera)}
                                slotIndex={index}
                                absIndex={viewStart + index}
                                dragEnabled={dragEnabled}
                                editMode={editMode}
                                spanStyle={tileSpanStyle(index)}
                                active={tileSheet.visible && tileSheet.slotIndex === index}
                            >
                                <ZoomableContainer
                                    isEview={isEviewMode}
                                    disabled={!camera.enabled || editMode}
                                    onDoubleClick={camera.onDoubleClick}
                                    onContextMenu={(e) => handleContextMenu(e, index, camera)}
                                    style={{ width: '100%', height: '100%' }}
                                >
                                    <CameraStreamContainerWs
                                        cameraId={camera.id}
                                        wsUrl={camera.wsUrl}
                                        camName={camera.camName}
                                        enabled={camera.enabled}
                                        deviceOnline={camera.deviceOnline}
                                        profile={decoderProfile}
                                        slotIndex={index}
                                        noJpegBase
                                        onDoubleClick={camera.onDoubleClick}
                                        onContextMenu={(e) => handleContextMenu(e, index, camera)}
                                        onFirstFrame={() => handleWsFirstFrame(getCameraKey(camera))}
                                        onConnecting={() => handleWsConnecting(getCameraKey(camera))}
                                        // The proxy refused or ended the stream (e.g. the device is no
                                        // longer this account's): re-read the session's access now.
                                        onServerStop={() => props.refreshCloudAccess({ force: true })}
                                    />
                                </ZoomableContainer>
                                {/* First-frame spinner (David): an online, enabled cloud cell shows a
                                    loading spinner while the WS connects + decodes its first frame, so a
                                    connecting cam reads as "loading" rather than a bare black tile. Clears
                                    on the first frame (wsStreaming) and never shows for an offline device. */}
                                {camera.enabled && camera.deviceOnline && wsStreaming[getCameraKey(camera)] !== true && (
                                    <TileLoadingSpinner />
                                )}
                                {/* Arrange mode → ✕ clears the slot; otherwise the ⋯ tile-actions
                                    menu (both hidden on desktop via CSS). */}
                                {editMode ? (
                                    <button
                                        className="live-tile-remove"
                                        aria-label="Clear slot"
                                        onPointerDown={(e) => e.stopPropagation()}
                                        onClick={(e) => { e.stopPropagation(); assignCameraToSlot(viewStart + index, null); }}
                                    >×</button>
                                ) : (
                                    <button
                                        className="live-tile-actions-btn"
                                        aria-label="Camera actions"
                                        onPointerDown={(e) => e.stopPropagation()}
                                        onClick={(e) => { e.stopPropagation(); handleOpenTileSheet(index, camera, e.currentTarget.getBoundingClientRect()); }}
                                    ><FaEllipsisH /></button>
                                )}
                            </DraggableTile>
                        );
                    }

                    const useWs = streamTypes[getCameraKey(camera)] === 'ws';
                    const isWsStreaming = wsStreaming[getCameraKey(camera)] === true;

                    // Show the JPEG layer when WS isn't streaming — AND always for an empty/disabled slot.
                    // An empty slot (camera.enabled false) has no WS stream, so its placeholder (the DIVIDIA
                    // card, rendered inside this JPEG layer) must never be hidden behind a STALE
                    // wsStreaming/streamTypes entry left over from a camera that occupied this slot index in
                    // a previous view — that stale state was collapsing the placeholder to 0×0 and exposing
                    // the bare black tile (the "black empty cell" bug). #86bb7phhh (David 2026-08-17).
                    const showJpeg = !camera.enabled || !useWs || !isWsStreaming;

                    return (
                        <DraggableTile
                            key={getCameraKey(camera)}
                            slotIndex={index}
                            absIndex={viewStart + index}
                            dragEnabled={dragEnabled}
                                editMode={editMode}
                            spanStyle={tileSpanStyle(index)}
                            active={tileSheet.visible && tileSheet.slotIndex === index}
                        >
                            <ZoomableContainer
                                isEview={isEviewMode}
                                disabled={!camera.enabled || editMode}
                                onDoubleClick={camera.onDoubleClick}
                                onContextMenu={(e) => handleContextMenu(e, index, camera)}
                                style={{ width: '100%', height: '100%' }}
                            >
                                {/* JPEG layer — always rendered, never unmounted.
                                    Hidden via CSS when WS is streaming. This prevents
                                    stagger delay re-running on WS retry cycles. */}
                                <div style={{ display: showJpeg ? 'block' : 'none', width: '100%', height: '100%' }}>
                                    <CameraStreamContainer
                                        enabled={camera}
                                        camNum={camera.bID}
                                        height="100%"
                                        width="100%"
                                        slotIndex={index}
                                        paused={!showJpeg}
                                        onDoubleClick={camera.onDoubleClick}
                                        onContextMenu={(e) => handleContextMenu(e, index, camera)}
                                        onFirstLoad={() => setAwaitingFirstFrameAfterJump(false)}
                                        showOverlay={false}
                                    />
                                </div>

                                {/* WS layer */}
                                {useWs && camera.enabled && (
                                    <CameraStreamContainerWs
                                        cameraId={camera.id}
                                        wsUrl={camera.wsUrl}
                                        camName={camera.camName}
                                        enabled={camera.enabled}
                                        profile={decoderProfile}
                                        slotIndex={index}
                                        onDoubleClick={camera.onDoubleClick}
                                        onContextMenu={(e) => handleContextMenu(e, index, camera)}
                                        onFallbackNeeded={() => handleWsFailure(getCameraKey(camera))}
                                        onFirstFrame={() => handleWsFirstFrame(getCameraKey(camera))}
                                        onConnectionChange={(status) => {
                                            if (status === 'connected') handleWsSuccess(getCameraKey(camera));
                                        }}
                                        onMetadataUpdate={(metadata) => {
                                            if (metadata?.time) camTimesRef.current[getCameraKey(camera)] = metadata.time;
                                        }}
                                        showOverlay={false}
                                    />
                                )}
                            </ZoomableContainer>

                            {/* Overlay outside zoom — timestamp top-left, name bottom-left */}
                            {camera.enabled && (
                                <CameraInfoOverlay
                                    camName={camera.camName}
                                    timestamp={tileTimestamp(camera)}
                                />
                            )}
                            {/* Arrange mode → ✕ clears the slot; otherwise the ⋯ tile-actions
                                menu (both hidden on desktop via CSS). */}
                            {editMode ? (
                                <button
                                    className="live-tile-remove"
                                    aria-label="Clear slot"
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onClick={(e) => { e.stopPropagation(); assignCameraToSlot(viewStart + index, null); }}
                                >×</button>
                            ) : (
                                <button
                                    className="live-tile-actions-btn"
                                    aria-label="Camera actions"
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onClick={(e) => { e.stopPropagation(); handleOpenTileSheet(index, camera, e.currentTarget.getBoundingClientRect()); }}
                                ><FaEllipsisH /></button>
                            )}
                        </DraggableTile>
                    );
                })}
            </div>{/* ip-camera-grid */}
            </div>{/* live-arrange-canvas */}
            </div>{/* live-editwrap */}
            {/* Floating drag preview — a boxed chip that follows the pointer/finger while
                dragging a camera (tree → tile) or a tile (swap), so the drag is visible. */}
            <DragOverlay dropAnimation={null}>
                {activeDrag ? (
                    <div className="live-drag-preview">
                        <span className="live-drag-preview__num">{String(activeDrag.num).padStart(2, '0')}</span>
                        <span className="live-drag-preview__name">{activeDrag.name}</span>
                    </div>
                ) : null}
            </DragOverlay>
            </DndContext>

            {/* Jump loading overlay — single overlay covering the entire grid */}
            {(isJumping || awaitingFirstFrameAfterJump) && (
                <div style={styles.jumpingOverlay}>
                    <div style={styles.jumpingSpinner}></div>
                    <span style={styles.jumpingText}>
                        {targetSystemName
                            ? `Switching server: ${targetSystemName}...`
                            : 'Switching server...'}
                    </span>
                </div>
            )}

            {/* Camera selection dialog */}
            {selectDialog.visible && (
                <CameraSelectDialog
                    visible={true}
                    slotIndex={selectDialog.slotIndex}
                    onSelectCamera={handleSelectCamera}
                    onClose={handleCloseSelectDialog}
                />
            )}

            {/* ⋯ tile-actions — bottom sheet on mobile, anchored popover on desktop (anchor set). */}
            <TileActionsSheet
                visible={tileSheet.visible}
                slotIndex={tileSheet.slotIndex}
                camera={tileSheet.camera}
                anchor={tileSheet.anchor}
                infoOn={ovPrefs.info}
                metaOn={ovPrefs.metadata}
                isCloud={getProductMode({ productMode }) === 'cloud'}
                onSnapshot={handleTileSnapshot}
                onSnapshotAll={handleSnapshotAll}
                onAssign={handleTileAssign}
                onClear={handleTileClear}
                onToggleInfo={handleTileToggleInfo}
                onToggleMeta={handleTileToggleMeta}
                onClose={handleCloseTileSheet}
            />
        </div>
    );
};

const mapStateToProps = state => ({
    conf: state.config.conf,
    currentCamView: state.camera.currentCamView,
    fSingle: state.video.fSingle,
    singleCamSelected: state.video.singleCamSelected,
    cameras: state.server.cameras,
    allCameras: state.server.allCameras,
    sSess: state.auth.sSess,
    // Pulled in for the cross-NVR WS auth fallback (see buildEviewWebSocketUrl).
    // Child NVRs don't honor the controller's session token, so we fall back
    // to ?user=&pass= on the URL — matching Java dview LiveStream.java.
    username: state.auth.username,
    password: state.auth.password,
    isJumping: state.navigation.isJumping,
    targetSystemName: state.navigation.targetSystemName,
    // eView mode support
    fEview: state.server.fEview,
    useEviewLogin: state.auth.useEviewLogin,
    serverSessions: state.server.serverSessions || {},
    nvrTimestamp: state.utility.nvrTimestamp,
    productMode: state.productMode
});

export default connect(mapStateToProps, { setSingleView, assignCameraToSlot, swapCameraSlots, refreshCloudAccess })(VideoViewWs);

const styles = {
    containerStyle: {
        display: 'grid',
        gap: 0,
        padding: 0,
        width: "100%",
        height: "100%",
        backgroundColor: '#000',
        boxSizing: 'border-box',
        minHeight: '200px',
    },
    jumpingOverlay: {
        // Solid (not translucent). selectView's SET_ALL_CAMERAS fills the
        // grid with 64 placeholder cells (mostly fEnable=false → dtplaceholder.gif)
        // before any real frame arrives; a translucent overlay let those bleed
        // through and looked like the jump bug recurring. ClickUp 86baduzfv.
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#000',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10,
    },
    jumpingSpinner: {
        width: 40,
        height: 40,
        border: '4px solid rgba(255, 255, 255, 0.3)',
        borderTopColor: '#fff',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        marginBottom: 12,
    },
    jumpingText: {
        color: '#fff',
        fontSize: 14,
        fontWeight: 500,
        textAlign: 'center',
    },
};


